// src/pages/OutreachTab.jsx
//
// PHA Outreach — widgetboss only.
//
// Reads the outreach_prospects / outreach_events tables in the compliance DB
// (housing-compliance-service /api/outreach/admin/*). The roster comes from
// HUD's PUBLIC_HOUSING_AUTHORITY layer, loaded by the outreach-tool importer;
// everything below the stats is pipeline state worked from this screen.
//
// Charts are hand-rolled divs — the dashboard has no chart dependency.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Target, Users, Mail, Send, MessageSquare, CalendarCheck, Trophy,
  RefreshCw, Search, Download, ChevronLeft, ChevronRight, Building2,
  AlertTriangle, Clock, Phone, Globe, StickyNote, Ban, Activity, MapPin,
} from 'lucide-react';

const COMPLIANCE_API_URL =
  process.env.REACT_APP_COMPLIANCE_API ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3200'
    : 'https://housing-compliance-service.onrender.com');

const ADMIN_KEY =
  process.env.REACT_APP_COMPLIANCE_ADMIN_KEY || process.env.REACT_APP_ADMIN_API_KEY || '';

function outreachUrl(path, params = {}) {
  const url = new URL(`${COMPLIANCE_API_URL}/api/outreach${path}`, window.location.origin);
  if (ADMIN_KEY) url.searchParams.set('key', ADMIN_KEY);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined && v !== 'all') url.searchParams.set(k, v);
  });
  return url.toString();
}

async function outreachFetch(path, params = {}, options = {}) {
  const res = await fetch(outreachUrl(path, params), options);
  if (!res.ok) throw new Error(`Outreach API error: ${res.status}`);
  return res.json();
}

// ─── Vocabulary ─────────────────────────────────────────────
const STATUS_META = {
  new:            { label: 'New',            badge: 'bg-gray-100 text-gray-700',    bar: 'bg-gray-300' },
  queued:         { label: 'Queued',         badge: 'bg-indigo-100 text-indigo-800', bar: 'bg-indigo-400' },
  contacted:      { label: 'Contacted',      badge: 'bg-amber-100 text-amber-800',  bar: 'bg-amber-400' },
  replied:        { label: 'Replied',        badge: 'bg-blue-100 text-blue-800',    bar: 'bg-blue-500' },
  meeting:        { label: 'Meeting',        badge: 'bg-purple-100 text-purple-800', bar: 'bg-purple-500' },
  won:            { label: 'Won',            badge: 'bg-green-100 text-green-800',  bar: 'bg-green-500' },
  lost:           { label: 'Lost',           badge: 'bg-red-100 text-red-700',      bar: 'bg-red-400' },
  do_not_contact: { label: 'Do not contact', badge: 'bg-gray-200 text-gray-600',    bar: 'bg-gray-400' },
};
const STATUS_ORDER = ['new', 'queued', 'contacted', 'replied', 'meeting', 'won', 'lost', 'do_not_contact'];

const TIER_META = {
  A: { label: 'A', hint: '5,000+ units',   dot: 'bg-blue-600',  badge: 'bg-blue-100 text-blue-800' },
  B: { label: 'B', hint: '1,000–4,999',    dot: 'bg-cyan-600',  badge: 'bg-cyan-100 text-cyan-800' },
  C: { label: 'C', hint: '250–999 / $3M+', dot: 'bg-teal-600',  badge: 'bg-teal-100 text-teal-800' },
  D: { label: 'D', hint: 'under 250',      dot: 'bg-gray-400',  badge: 'bg-gray-100 text-gray-600' },
};

const CONFIDENCE_META = {
  high:   { label: 'High',   badge: 'bg-green-100 text-green-800' },
  medium: { label: 'Medium', badge: 'bg-amber-100 text-amber-800' },
  low:    { label: 'Low',    badge: 'bg-gray-100 text-gray-600' },
};

const EVENT_META = {
  email_sent:     { label: 'Email sent',   icon: Send,          tone: 'text-blue-600' },
  reply_received: { label: 'Reply',        icon: MessageSquare, tone: 'text-green-600' },
  call:           { label: 'Call',         icon: Phone,         tone: 'text-indigo-600' },
  meeting:        { label: 'Meeting',      icon: CalendarCheck, tone: 'text-purple-600' },
  note:           { label: 'Note',         icon: StickyNote,    tone: 'text-gray-500' },
  bounce:         { label: 'Bounce',       icon: AlertTriangle, tone: 'text-amber-600' },
  unsubscribe:    { label: 'Unsubscribed', icon: Ban,           tone: 'text-red-600' },
  declined:       { label: 'Declined',     icon: Ban,           tone: 'text-gray-500' },
  status_change:  { label: 'Stage change', icon: Activity,      tone: 'text-gray-500' },
};

// ─── Formatting ─────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString();
const pct = (part, whole) => (whole ? Math.round((Number(part) / Number(whole)) * 100) : 0);
const money = (n) => {
  const v = Number(n || 0);
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v}`;
};
const fmtDate = (iso) => (iso
  ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '—');
const fmtDay = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ─── Small pieces ───────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, tone = 'blue' }) => {
  const tones = {
    blue: 'bg-blue-50 text-blue-600', green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600', purple: 'bg-purple-50 text-purple-600',
    gray: 'bg-gray-100 text-gray-600', indigo: 'bg-indigo-50 text-indigo-600',
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className={`p-1.5 rounded-lg ${tones[tone]}`}><Icon className="h-4 w-4" /></span>
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-2xl font-bold text-gray-900 leading-none">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1.5">{sub}</div>}
    </div>
  );
};

const BarRow = ({ label, hint, value, max, count, color = 'bg-blue-500' }) => (
  <div className="flex items-center gap-3 text-sm">
    <div className="w-28 shrink-0 truncate text-gray-700" title={hint || label}>{label}</div>
    <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${max ? (value / max) * 100 : 0}%` }} />
    </div>
    <div className="w-24 shrink-0 text-right text-gray-600 tabular-nums text-xs">{count}</div>
  </div>
);

const Panel = ({ title, icon: Icon, right, children, className = '' }) => (
  <div className={`bg-white border border-gray-200 rounded-xl p-5 ${className}`}>
    <div className="flex items-center justify-between mb-4">
      <h4 className="font-semibold text-gray-900 flex items-center gap-2 text-sm">
        {Icon && <Icon className="h-4 w-4 text-gray-400" />} {title}
      </h4>
      {right}
    </div>
    {children}
  </div>
);

// ═══════════════════════════════════════════════════════════
// Stats block
// ═══════════════════════════════════════════════════════════
const OutreachStats = ({ stats }) => {
  const t = stats.totals;

  // Funnel: how far the roster has moved, widest stage first.
  const statusCounts = useMemo(() => {
    const map = Object.fromEntries(stats.byStatus.map((r) => [r.status, r.count]));
    return STATUS_ORDER.map((s) => ({ status: s, count: map[s] || 0 })).filter((r) => r.count > 0);
  }, [stats.byStatus]);

  // 30-day axis, zero-filled so gaps read as quiet days rather than missing data.
  const days = useMemo(() => {
    const byDay = Object.fromEntries(stats.activity.map((r) => [r.day, r]));
    const out = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toLocaleDateString('en-CA');
      out.push({ day: key, emails: byDay[key]?.emails || 0, replies: byDay[key]?.replies || 0, total: byDay[key]?.total || 0 });
    }
    return out;
  }, [stats.activity]);
  const maxDay = Math.max(1, ...days.map((d) => d.total));
  const activeMonth = days.reduce((a, d) => a + d.total, 0);

  const maxTierUnits = Math.max(1, ...stats.byTier.map((r) => Number(r.units)));
  const maxStateUnits = Math.max(1, ...stats.byState.map((r) => Number(r.units)));
  const maxSource = Math.max(1, ...stats.byNameSource.map((r) => r.count));

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={Building2} label="Prospects" value={fmt(t.prospects)}
          sub={`${fmt(t.total_units)} units · ${money(t.total_expenses)} spend`} />
        <StatCard icon={Users} label="Named EDs" value={fmt(t.named)} tone="indigo"
          sub={`${pct(t.named, t.prospects)}% of roster · ${fmt(t.named_high)} high confidence`} />
        <StatCard icon={Mail} label="Direct emails" value={fmt(t.with_ed_email)} tone="gray"
          sub={`${pct(t.with_ed_email, t.prospects)}% reachable · ${fmt(t.with_s8)} with an S8 CC`} />
        <StatCard icon={Send} label="Contacted" value={fmt(t.contacted)} tone="amber"
          sub={`${fmt(t.total_touches)} touches logged`} />
        <StatCard icon={MessageSquare} label="Replies" value={fmt(t.replied)} tone="blue"
          sub={t.contacted ? `${pct(t.replied, t.contacted)}% reply rate` : 'no sends yet'} />
        <StatCard icon={Trophy} label="Meetings / won" value={`${fmt(t.meetings)} / ${fmt(t.won)}`} tone="green"
          sub={`${fmt(t.do_not_contact)} do not contact`} />
      </div>

      {/* Funnel */}
      <Panel title="Pipeline" icon={Target}
        right={<span className="text-xs text-gray-400">{fmt(t.prospects)} prospects</span>}>
        <div className="flex rounded-lg overflow-hidden h-8 mb-3">
          {statusCounts.map(({ status, count }) => (
            <div key={status} className={`${STATUS_META[status].bar} h-full flex items-center justify-center`}
              style={{ width: `${(count / t.prospects) * 100}%` }}
              title={`${STATUS_META[status].label}: ${fmt(count)}`}>
              {count / t.prospects > 0.08 && (
                <span className="text-[11px] font-semibold text-white/90 px-1 truncate">{fmt(count)}</span>
              )}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {statusCounts.map(({ status, count }) => (
            <div key={status} className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className={`h-2.5 w-2.5 rounded-sm ${STATUS_META[status].bar}`} />
              {STATUS_META[status].label}
              <span className="font-semibold text-gray-900">{fmt(count)}</span>
            </div>
          ))}
        </div>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Tier table */}
        <Panel title="ICP tiers" icon={Building2}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                <th className="pb-2 font-medium">Tier</th>
                <th className="pb-2 font-medium text-right">PHAs</th>
                <th className="pb-2 font-medium text-right">Named</th>
                <th className="pb-2 font-medium text-right">Contacted</th>
                <th className="pb-2 font-medium text-right">Replied</th>
                <th className="pb-2 font-medium text-right">Units</th>
              </tr>
            </thead>
            <tbody>
              {stats.byTier.map((r) => {
                const meta = TIER_META[r.tier] || TIER_META.D;
                return (
                  <tr key={r.tier} className="border-b border-gray-50 last:border-0">
                    <td className="py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
                        <span className="font-semibold text-gray-900">{meta.label}</span>
                        <span className="text-xs text-gray-400">{meta.hint}</span>
                      </span>
                    </td>
                    <td className="py-2 text-right tabular-nums text-gray-900">{fmt(r.count)}</td>
                    <td className="py-2 text-right tabular-nums text-gray-600">
                      {fmt(r.named)} <span className="text-xs text-gray-400">{pct(r.named, r.count)}%</span>
                    </td>
                    <td className="py-2 text-right tabular-nums text-gray-600">{fmt(r.contacted)}</td>
                    <td className="py-2 text-right tabular-nums text-gray-600">{fmt(r.replied)}</td>
                    <td className="py-2 text-right tabular-nums text-gray-600">{fmt(r.units)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-4 space-y-2">
            {stats.byTier.map((r) => (
              <BarRow key={r.tier} label={`Tier ${r.tier}`} hint={TIER_META[r.tier]?.hint}
                value={Number(r.units)} max={maxTierUnits} count={`${fmt(r.units)} units`}
                color={(TIER_META[r.tier] || TIER_META.D).dot} />
            ))}
          </div>
        </Panel>

        {/* States */}
        <Panel title="Where the units are" icon={MapPin}
          right={<span className="text-xs text-gray-400">top 12 by portfolio</span>}>
          <div className="space-y-2">
            {stats.byState.map((r) => (
              <BarRow key={r.state} label={r.state} value={Number(r.units)} max={maxStateUnits}
                count={`${fmt(r.units)} · ${r.count} PHAs`} color="bg-blue-500" />
            ))}
          </div>
        </Panel>

        {/* Name coverage */}
        <Panel title="Director-name coverage" icon={Users}
          right={<span className="text-xs text-gray-400">{pct(t.named, t.prospects)}% named</span>}>
          <div className="space-y-2">
            {stats.byNameSource.map((r) => (
              <BarRow key={r.source} label={r.source.replace(/[-_]/g, ' ')} value={r.count} max={maxSource}
                count={`${fmt(r.count)} · ${pct(r.count, t.prospects)}%`}
                color={r.source === 'website' || r.source === 'email' ? 'bg-green-500' : 'bg-gray-300'} />
            ))}
          </div>
          {t.needs_review > 0 && (
            <p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              {fmt(t.needs_review)} names are medium confidence — check one before it goes in a first line.
            </p>
          )}
        </Panel>

        {/* Activity */}
        <Panel title="Activity, last 30 days" icon={Activity}
          right={<span className="text-xs text-gray-400">{fmt(activeMonth)} events</span>}>
          {activeMonth === 0 ? (
            <div className="text-center py-10 text-sm text-gray-400">
              Nothing logged yet. Touches you record on a prospect show up here.
            </div>
          ) : (
            <>
              <div className="flex items-end gap-[3px] h-32">
                {days.map((d) => (
                  <div key={d.day} className="flex-1 flex flex-col justify-end h-full group relative"
                    title={`${fmtDay(d.day)} · ${d.emails} sent, ${d.replies} replies`}>
                    <div className="bg-green-500 rounded-t-sm" style={{ height: `${(d.replies / maxDay) * 100}%` }} />
                    <div className="bg-blue-500" style={{ height: `${((d.total - d.replies) / maxDay) * 100}%` }} />
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2 text-[11px] text-gray-400">
                <span>{fmtDay(days[0].day)}</span><span>{fmtDay(days[days.length - 1].day)}</span>
              </div>
              <div className="flex gap-4 mt-2 text-xs text-gray-600">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-blue-500" /> touches</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-green-500" /> replies</span>
              </div>
            </>
          )}
        </Panel>
      </div>

      {/* Follow-ups + recent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="Follow-ups due" icon={Clock}
          right={<span className="text-xs text-gray-400">next 7 days</span>}>
          {stats.followups.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">Nothing scheduled.</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {stats.followups.map((f) => (
                <div key={f.id} className="py-2 flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">{f.pha_name}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {f.exec_name || f.exec_dir_email || '—'} · {f.state} · Tier {f.icp_tier}
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className={`text-xs font-medium ${new Date(f.next_follow_up_on) <= new Date() ? 'text-red-600' : 'text-gray-600'}`}>
                      {fmtDate(f.next_follow_up_on)}
                    </div>
                    <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-[11px] ${STATUS_META[f.status]?.badge}`}>
                      {STATUS_META[f.status]?.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Recent activity" icon={Activity}>
          {stats.recentEvents.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">No activity logged yet.</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {stats.recentEvents.map((e) => {
                const meta = EVENT_META[e.event_type] || EVENT_META.note;
                const Icon = meta.icon;
                return (
                  <div key={e.id} className="py-2 flex items-start gap-2.5 text-sm">
                    <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${meta.tone}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-gray-900 truncate">
                        <span className="font-medium">{meta.label}</span> · {e.pha_name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {e.subject || e.outcome || `${e.state} · Tier ${e.icp_tier}`}
                        {e.actor ? ` · ${e.actor}` : ''}
                      </div>
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">{fmtDate(e.occurred_at)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Scheduler: queue a batch, review it, approve it
// ═══════════════════════════════════════════════════════════
// Nothing here sends by itself. "Queue next batch" stages a draft; a draft
// never sends. Approving hands it to the server's scheduler, which mails the
// first email on the next launch day (Mondays, 06:30-08:00 recipient time),
// then the later steps on days 3, 10 and 21, and the call hand-off on day 24,
// for anyone who stays silent. Approving also counts as the names review:
// every agency name and jurisdiction in the batch is editable right here.
const BATCH_BADGE = {
  draft: 'bg-gray-100 text-gray-700', approved: 'bg-indigo-100 text-indigo-800',
  sent: 'bg-green-100 text-green-800', cancelled: 'bg-gray-100 text-gray-400',
};
const TOPIC_LABEL = { maintenance: 'Maintenance', assistant: 'Assistant', recert: 'Recerts', ua: 'Utility allowance' };
const ARM_LABEL = { A: 'A · maintenance first', B: 'B · recerts first', V: 'V · voucher-only, three emails' };
const VERIFY_META = {
  pending:     { label: 'Checking site…', badge: 'bg-gray-100 text-gray-500' },
  verified:    { label: 'Verified',       badge: 'bg-green-100 text-green-800' },
  flagged:     { label: 'Needs a look',   badge: 'bg-amber-100 text-amber-800' },
  unreachable: { label: 'Site unreachable', badge: 'bg-gray-200 text-gray-700' },
};

// One row's verification verdict with the evidence behind it.
const Verdict = ({ i }) => {
  const [open, setOpen] = useState(false);
  const m = VERIFY_META[i.verify_status];
  if (!m) return null;
  const ev = i.verify_evidence;
  return (
    <div className="mt-1">
      <button onClick={() => setOpen(!open)} className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${m.badge}`}>{m.label}</button>
      {i.verify_note && <span className="ml-1.5 text-gray-600">{i.verify_note}</span>}
      {open && ev && (
        <div className="mt-1 text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
          {ev.director && <div>Site names: <span className="text-gray-900">{ev.director}</span>{ev.title ? `, ${ev.title}` : ''}</div>}
          {ev.quote && <div className="italic">“{ev.quote}”</div>}
          {ev.quote_url && <a href={ev.quote_url} target="_blank" rel="noreferrer" className="text-blue-600 break-all">{ev.quote_url}</a>}
          {!ev.quote && ev.pages?.length > 0 && <div>Pages read: {ev.pages.map((u, k) => <a key={k} href={u} target="_blank" rel="noreferrer" className="text-blue-600 mr-2 break-all">{u.replace(/^https?:\/\//, '')}</a>)}</div>}
          {ev.error && <div className="text-red-700">{ev.error}</div>}
        </div>
      )}
    </div>
  );
};

// One editable name field; saves on blur when changed.
const NameField = ({ value, placeholder, onSave, flag }) => {
  const [v, setV] = useState(value || '');
  useEffect(() => { setV(value || ''); }, [value]);
  return (
    <input value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder}
      onBlur={() => { if (v !== (value || '')) onSave(v); }}
      className={`w-full px-1.5 py-0.5 text-xs border rounded ${flag ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`} />
  );
};

const SchedulerPanel = ({ actor, onChanged }) => {
  const [status, setStatus] = useState(null);
  const [batch, setBatch] = useState(null);
  const [recent, setRecent] = useState([]);
  const [size, setSize] = useState('');
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [sample, setSample] = useState(null);   // { arm, step }

  const post = (path, body = {}) => fetch(outreachUrl(path), {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ actor, ...body }),
  }).then(async (res) => ({ ok: res.ok, data: await res.json().catch(() => ({})) }));

  const load = useCallback(async () => {
    try {
      const [s, list] = await Promise.all([outreachFetch('/admin/scheduler'), outreachFetch('/admin/batches')]);
      setStatus(s);
      setRecent(list.batches || []);
      setBatch(s.openBatchId ? await outreachFetch(`/admin/batches/${s.openBatchId}`) : null);
    } catch (err) {
      console.error('❌ Load scheduler error:', err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // While the verifier is working through a draft, refresh every few seconds.
  useEffect(() => {
    if (!batch || !batch.verifying) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [batch, load]);

  const run = async (label, fn) => {
    setBusy(label);
    setNote('');
    try {
      const msg = await fn();
      if (msg) setNote(msg);
      await load();
      onChanged();
    } catch (err) {
      setNote(err.message || 'Failed.');
    } finally {
      setBusy('');
    }
  };

  const queue = () => run('queue', async () => {
    const r = await post('/admin/batches', size ? { size: Number(size) } : {});
    if (!r.ok) return r.data.detail || 'Nothing to queue.';
    return r.data.created ? `Drafted batch #${r.data.batch.id} with ${r.data.items.length} prospects. Checking each agency's website now.` : 'A batch is already open.';
  });
  const approveReady = () => run('approve-ready', async () => {
    const r = await post(`/admin/batches/${batch.batch.id}/approve-ready`);
    if (!r.ok) return r.data.error || 'Could not approve.';
    return r.data.moved ? `Approved ${r.data.moved} ready row(s)${r.data.draftBatchId ? '; the rest stay here for a look' : ''}.` : 'No row is ready yet.';
  });
  const verify = () => run('verify', async () => {
    const r = await post(`/admin/batches/${batch.batch.id}/verify`, { force: true });
    return r.ok ? 'Checking each agency website again…' : 'Could not start verification.';
  });
  const approve = () => run('approve', async () => {
    const r = await post(`/admin/batches/${batch.batch.id}/approve`);
    return r.ok ? 'Approved. First emails go out on the next launch day.' : (r.data.error || 'Could not approve.');
  });
  const cancel = () => run('cancel', async () => {
    const r = await post(`/admin/batches/${batch.batch.id}/cancel`);
    return r.ok ? 'Batch cancelled. Unsent prospects are back to New.' : (r.data.error || 'Could not cancel.');
  });
  const remove = (prospectId) => run(`remove-${prospectId}`, async () => {
    const r = await post(`/admin/batches/${batch.batch.id}/remove`, { prospectId });
    return r.ok ? '' : 'Could not remove that row.';
  });
  const saveName = (prospectId, field, value) => run(`name-${prospectId}`, async () => {
    const r = await post(`/admin/prospects/${prospectId}/names`, { [field]: value, reviewed: true });
    return r.ok ? '' : 'Could not save that name.';
  });
  const markReviewed = (prospectId) => run(`reviewed-${prospectId}`, async () => {
    const r = await post(`/admin/prospects/${prospectId}/names`, { reviewed: true });
    return r.ok ? '' : 'Could not mark that row.';
  });
  const togglePause = () => run('pause', async () => {
    await post('/admin/scheduler/pause', { paused: !status.paused });
    return status.paused ? 'Resumed.' : 'Paused. No scheduled email will go out.';
  });
  const setArm = (arm) => run('arm', async () => {
    await post('/admin/scheduler/arm', { arm });
    return arm === 'alternate' ? 'New batches alternate between the two orders.' : `New batches use order ${arm}.`;
  });
  const tickNow = () => run('tick', async () => {
    const r = await post('/admin/scheduler/tick');
    const d = r.data;
    if (d.skipped) return `Scheduler skipped: ${d.skipped}.`;
    const errs = (d.errors || []).length ? ` · ${d.errors.length} error(s): ${d.errors[0]}` : '';
    return `Sent ${d.firstEmails} first email(s), ${d.laterSteps} later step(s), ${d.handoffs} call hand-off(s)${errs}`;
  });

  if (!status) return null;
  const w = status.window;
  const b = batch?.batch;
  const sampleEmail = sample && batch?.samples?.[sample.arm]?.find((x) => x.step === sample.step);

  return (
    <Panel title="Scheduler" icon={Clock}
      right={
        <div className="flex items-center gap-2">
          {note && <span className="text-xs text-gray-500 max-w-md truncate" title={note}>{note}</span>}
          <button onClick={tickNow} disabled={!!busy}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-gray-200 bg-white text-xs text-gray-700 hover:border-blue-400 hover:text-blue-700 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${busy === 'tick' ? 'animate-spin' : ''}`} /> Run now
          </button>
          <button onClick={togglePause} disabled={!!busy}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium disabled:opacity-50 ${
              status.paused ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'}`}>
            <Ban className="h-3.5 w-3.5" /> {status.paused ? 'Resume sending' : 'Pause sending'}
          </button>
        </div>
      }>
      {/* Where things stand */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs mb-4">
        <div className="bg-gray-50 rounded-lg px-3 py-2">
          <div className="text-gray-500">State</div>
          <div className={`font-semibold ${status.paused ? 'text-red-700' : 'text-gray-900'}`}>
            {status.paused ? 'Paused' : 'Running'} · {status.live ? 'LIVE' : 'DEV (test rows only)'}
          </div>
          {status.pausedReason && <div className="text-red-700 mt-0.5">{status.pausedReason}</div>}
        </div>
        <div className="bg-gray-50 rounded-lg px-3 py-2">
          <div className="text-gray-500">Next launch</div>
          <div className="font-semibold text-gray-900">{status.launch.nextDay ? fmtDay(status.launch.nextDay) : '—'} · {status.launch.size} prospects</div>
          <div className="text-gray-400">week {status.launch.week}{status.launch.held ? ' · size held: bounces' : ''} · {w.start}–{w.end} {w.tz}</div>
        </div>
        <div className="bg-gray-50 rounded-lg px-3 py-2">
          <div className="text-gray-500">Sent today</div>
          <div className="font-semibold text-gray-900">{status.sentToday} of {status.dailyCap} cap</div>
          <div className="text-gray-400">bounces: {status.bounces.today} today · {status.bounces.percent14d}% of last {status.bounces.sent14d}</div>
        </div>
        <div className="bg-gray-50 rounded-lg px-3 py-2">
          <div className="text-gray-500">Later steps</div>
          <div className="font-semibold text-gray-900">{status.followUps.pending} pending · {status.followUps.due_now} due</div>
          <div className="text-gray-400 truncate" title={status.notify.join(', ')}>
            hand-offs go to {status.notify.length ? status.notify.join(', ') : 'nobody (OUTREACH_NOTIFY unset)'}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg px-3 py-2">
          <div className="text-gray-500">Email order for new batches</div>
          <select value={status.defaultArm} onChange={(e) => setArm(e.target.value)} disabled={!!busy}
            className="mt-0.5 text-xs border border-gray-200 rounded px-1 py-0.5 bg-white">
            <option value="alternate">Alternate A / B (the test)</option>
            <option value="A">A: maintenance first</option>
            <option value="B">B: recerts first</option>
          </select>
        </div>
      </div>

      {/* The A/B test so far, real prospects only */}
      {status.arms?.length > 0 && (
        <div className="mb-4 overflow-x-auto">
          <table className="text-xs">
            <thead className="text-gray-500 text-left">
              <tr>{['Order', 'Prospects', 'Launched', 'Finished', 'Replied', 'Reply rate', 'Advanced', 'Declined', 'Opted out', 'Bounced'].map((h) => (
                <th key={h} className="pr-4 py-1 font-medium">{h}</th>))}</tr>
            </thead>
            <tbody>
              {status.arms.map((a) => (
                <tr key={a.arm} className="border-t border-gray-100 text-gray-800">
                  <td className="pr-4 py-1 font-medium">{ARM_LABEL[a.arm] || a.arm}</td>
                  <td className="pr-4 py-1">{a.prospects}</td><td className="pr-4 py-1">{a.launched}</td><td className="pr-4 py-1">{a.finished}</td>
                  <td className="pr-4 py-1">{a.replied}</td><td className="pr-4 py-1 font-semibold">{a.replyRate === null ? '—' : `${a.replyRate}%`}</td>
                  <td className="pr-4 py-1">{a.advanced}</td><td className="pr-4 py-1">{a.declined}</td><td className="pr-4 py-1">{a.opted_out}</td><td className="pr-4 py-1">{a.bounced}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Open batch, or the button to make one */}
      {!b ? (
        <div className="flex items-center gap-2">
          <input type="number" min="1" max="200" value={size} onChange={(e) => setSize(e.target.value)}
            placeholder={String(status.launch.size)}
            className="w-20 px-2 py-1 text-sm border border-gray-200 rounded" />
          <button onClick={queue} disabled={!!busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50">
            <Users className="h-3.5 w-3.5" /> {busy === 'queue' ? 'Picking…' : 'Queue next batch'}
          </button>
          <span className="text-xs text-gray-500">Stages a draft for review. Nothing sends until you approve it.</span>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg">
          <div className="px-3 py-2 flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
            <span className="font-semibold text-sm text-gray-900">Batch #{b.id}</span>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${BATCH_BADGE[b.status]}`}>{b.status}</span>
            {b.is_test && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">TEST</span>}
            <span className="text-xs text-gray-500">
              {batch.items.length} prospects · {batch.sent} launched · {batch.pending} waiting
              {b.status === 'approved' && !b.is_test ? ` · launches ${status.launch.nextDay ? fmtDay(status.launch.nextDay) : 'next Monday'}` : ''}
            </span>
            {b.status === 'draft' && (
              <span className="text-xs">
                <span className="text-green-700">{batch.ready} ready</span>
                {' · '}
                <span className={batch.pending - batch.ready > 0 ? 'text-amber-700' : 'text-gray-400'}>{batch.pending - batch.ready} need a look</span>
                {batch.verifying > 0 && <span className="text-gray-500"> · checking {batch.verifying} site{batch.verifying === 1 ? '' : 's'}…</span>}
              </span>
            )}
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-gray-500">Read:</span>
              {Object.keys(batch.samples || {}).map((arm) => (batch.samples[arm] || []).map(({ step }) => (
                <button key={`${arm}${step}`} onClick={() => setSample(sample?.arm === arm && sample?.step === step ? null : { arm, step })}
                  className={`text-xs px-1.5 py-0.5 rounded border ${sample?.arm === arm && sample?.step === step ? 'bg-blue-600 text-white border-blue-600' : 'text-blue-700 border-blue-200 hover:bg-blue-50'}`}>
                  {arm}{step}
                </button>
              )))}
              {b.status === 'draft' && !b.is_test && (
                <button onClick={verify} disabled={!!busy || batch.verifying > 0} title="Check every agency website again"
                  className="px-2.5 py-1 text-xs text-gray-600 hover:text-blue-700 disabled:opacity-50">
                  Re-check sites
                </button>
              )}
              <button onClick={cancel} disabled={!!busy} className="px-2.5 py-1 text-xs text-gray-600 hover:text-red-700 disabled:opacity-50">
                Cancel batch
              </button>
              {b.status === 'draft' && batch.ready > 0 && batch.ready < batch.pending && (
                <button onClick={approveReady} disabled={!!busy || batch.verifying > 0}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded bg-green-600 text-white text-xs font-medium hover:bg-green-700 disabled:opacity-50">
                  <Send className="h-3.5 w-3.5" /> {busy === 'approve-ready' ? 'Approving…' : `Approve ${batch.ready} ready`}
                </button>
              )}
              {b.status === 'draft' && (
                <button onClick={approve} disabled={!!busy || batch.items.length === 0 || batch.verifying > 0}
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded text-xs font-medium disabled:opacity-50 ${
                    batch.ready === batch.pending ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-white text-green-700 border border-green-600 hover:bg-green-50'}`}>
                  <Send className="h-3.5 w-3.5" /> {busy === 'approve' ? 'Approving…' : `Approve all ${batch.items.length}`}
                </button>
              )}
            </div>
          </div>
          {sampleEmail && (
            <div className="px-3 py-2 border-b border-gray-200 text-xs">
              <div className="text-gray-500 mb-1">
                Order {sample.arm}, email {sample.step} ({TOPIC_LABEL[sampleEmail.topic]}) as it reads for {sampleEmail.for} · subject: {sampleEmail.subject || 'reply in the thread of email 1'}
              </div>
              <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-gray-900 bg-gray-50 border border-gray-200 rounded px-3 py-2 max-h-72 overflow-y-auto">{sampleEmail.text}</pre>
            </div>
          )}
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500 text-left">
                <tr>
                  <th className="px-3 py-1.5 font-medium">HUD record</th>
                  <th className="px-3 py-1.5 font-medium w-56">Agency, as it reads after "at"</th>
                  <th className="px-3 py-1.5 font-medium w-40">Jurisdiction (subject line)</th>
                  <th className="px-3 py-1.5 font-medium">First email</th>
                  <th className="px-3 py-1.5 font-medium">Notes</th>
                  <th className="px-3 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {[...batch.items].sort((a, c) => (a.ready === c.ready ? 0 : a.ready ? 1 : -1)).map((i) => (
                  <tr key={i.id} className={`border-t border-gray-100 align-top ${!i.ready && i.seq_step === 0 ? 'bg-amber-50/40' : ''}`}>
                    <td className="px-3 py-1.5">
                      <div className="text-gray-900 font-medium">{i.pha_name}</div>
                      <div className="text-gray-500">{i.city}, {i.state} · tier {i.icp_tier} · {fmt(i.portfolio_units)} units · {i.greeting}</div>
                      <div className="text-gray-500 break-all">{i.to.join(', ')}{i.cc.length ? <span className="text-gray-400"> · cc {i.cc.join(', ')}</span> : null}</div>
                      <Verdict i={i} />
                    </td>
                    <td className="px-3 py-1.5">
                      <NameField value={i.display_name} placeholder={i.pha_name} flag={!i.ready && !i.names_reviewed}
                        onSave={(v) => saveName(i.id, 'displayName', v)} />
                    </td>
                    <td className="px-3 py-1.5">
                      <NameField value={i.jurisdiction} placeholder={i.city} flag={!i.ready && !i.names_reviewed}
                        onSave={(v) => saveName(i.id, 'jurisdiction', v)} />
                    </td>
                    <td className="px-3 py-1.5 text-gray-700">
                      <div><span className="font-medium">{i.arm}</span> · {TOPIC_LABEL[i.topic]}{i.arm === 'V' ? <span className="text-gray-400"> · voucher-only, 3 emails</span> : null}</div>
                      <div className="text-gray-500">{i.subject}</div>
                    </td>
                    <td className="px-3 py-1.5">
                      {i.seq_step >= 1
                        ? <span className="text-green-700">launched</span>
                        : i.reasons.length
                          ? <span className="text-red-700">{i.reasons.join('; ')}</span>
                          : <span className="text-amber-700">{[...(i.names_reviewed ? [] : [i.names_note]), ...i.warnings].filter(Boolean).join('; ')}</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      {i.seq_step === 0 && !i.ready && (
                        <button onClick={() => markReviewed(i.id)} disabled={!!busy} title="I looked at this row; it can go as it stands"
                          className="text-green-600 hover:text-green-800 disabled:opacity-50 mr-2">✓</button>
                      )}
                      {i.seq_step === 0 && (
                        <button onClick={() => remove(i.id)} disabled={!!busy} title="Remove from this batch"
                          className="text-gray-400 hover:text-red-600 disabled:opacity-50">✕</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* History */}
      {recent.filter((r) => !b || r.id !== b.id).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5 text-xs">
          {recent.filter((r) => !b || r.id !== b.id).slice(0, 8).map((r) => (
            <span key={r.id} className={`px-2 py-0.5 rounded-full ${BATCH_BADGE[r.status]}`}
              title={`created ${fmtDate(r.created_at)}${r.approved_by ? ` · approved by ${r.approved_by}` : ''}`}>
              #{r.id} {r.status} · {r.sent}/{r.size} launched{r.replied ? ` · ${r.replied} replied` : ''}{r.is_test ? ' · test' : ''}
            </span>
          ))}
        </div>
      )}
    </Panel>
  );
};

// ═══════════════════════════════════════════════════════════
// Inbox: what came back to the outreach mailbox
// ═══════════════════════════════════════════════════════════
// The server reads the mailbox on a timer; the button forces a read now.
// Unmatched rows are inbound mail no prospect claimed — worth a human look.
const INBOX_KIND = {
  reply:       { label: 'Reply',        badge: 'bg-blue-100 text-blue-800' },
  unsubscribe: { label: 'Unsubscribe',  badge: 'bg-red-100 text-red-700' },
  bounce:      { label: 'Bounce',       badge: 'bg-amber-100 text-amber-800' },
  auto_reply:  { label: 'Auto-reply',   badge: 'bg-gray-100 text-gray-600' },
  declined:    { label: 'Declined',     badge: 'bg-gray-200 text-gray-700' },
  team_reply:  { label: 'Our reply',    badge: 'bg-indigo-100 text-indigo-800' },
  unmatched:   { label: 'Unmatched',    badge: 'bg-purple-100 text-purple-800' },
};

const InboxPanel = ({ actor, onChanged }) => {
  const [messages, setMessages] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await outreachFetch('/admin/inbox', { limit: 15 });
      setMessages(data.messages || []);
    } catch (err) {
      console.error('❌ Load inbox error:', err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    setNote('');
    try {
      const r = await outreachFetch('/admin/inbox/sync', {}, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ actor }),
      });
      const kinds = Object.entries(r.byKind || {}).map(([k, n]) => `${n} ${INBOX_KIND[k]?.label.toLowerCase() || k}`).join(', ');
      setNote(r.skipped ? `Skipped: ${r.skipped}` : `Scanned ${r.scanned}, new ${r.stored}${kinds ? ` (${kinds})` : ''}`);
      await load();
      onChanged();
    } catch (err) {
      console.error('❌ Inbox sync error:', err);
      setNote('Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Panel title="Inbox" icon={MessageSquare}
      right={
        <div className="flex items-center gap-2">
          {note && <span className="text-xs text-gray-500">{note}</span>}
          <button onClick={sync} disabled={syncing}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-gray-200 bg-white text-xs text-gray-700 hover:border-blue-400 hover:text-blue-700 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing…' : 'Sync inbox'}
          </button>
        </div>
      }>
      {messages.length === 0 ? (
        <p className="text-xs text-gray-400">Nothing received yet.</p>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
          {messages.map((m) => {
            const k = INBOX_KIND[m.kind] || INBOX_KIND.unmatched;
            return (
              <div key={m.id} className="flex items-start gap-2 text-xs border-b border-gray-100 pb-1.5 last:border-0">
                <span className={`shrink-0 mt-0.5 px-1.5 py-0.5 rounded font-medium ${k.badge}`}>{k.label}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-gray-900 truncate">
                    {m.pha_name ? <span className="font-medium">{m.pha_name}</span> : <span className="text-gray-500 italic">no prospect matched</span>}
                    {m.is_test && <span className="ml-1.5 text-[10px] font-semibold px-1 py-0.5 rounded bg-amber-100 text-amber-800">TEST</span>}
                    <span className="text-gray-400"> · {m.from_address}</span>
                  </div>
                  <div className="text-gray-500 truncate">{m.subject}{m.snippet ? ` — ${m.snippet.replace(/\s+/g, ' ').slice(0, 90)}` : ''}</div>
                </div>
                <span className="shrink-0 text-gray-400">{fmtDate(m.received_at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
};

// ═══════════════════════════════════════════════════════════
// Send the intro email from the outreach mailbox
// ═══════════════════════════════════════════════════════════
// Preview first, always: the server renders exactly what would go out, says
// whether the prospect is eligible, and whether the recipient guardrail would
// let it leave. Nothing is sent until the confirm button in the preview.
const SendIntroEmail = ({ prospect, actor, onSent }) => {
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const loadPreview = async () => {
    setBusy(true);
    setResult(null);
    try {
      setPreview(await outreachFetch(`/admin/prospects/${prospect.id}/preview`));
      setOpen(true);
    } catch (err) {
      console.error('❌ Preview error:', err);
      alert('Could not build the preview.');
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true);
    try {
      const res = await fetch(outreachUrl(`/admin/prospects/${prospect.id}/send`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actor }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResult({ ok: false, text: data.detail || data.reasons?.join('; ') || data.error || `HTTP ${res.status}` });
        return;
      }
      setResult({ ok: true, text: `Sent ${fmtDate(data.message.sent_at)} · Message-ID ${data.message.internet_message_id}` });
      onSent();
    } catch (err) {
      setResult({ ok: false, text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const canSend = preview && preview.eligibility.ok && preview.guardrail.allowed && preview.guardrail.configured;

  return (
    <div>
      <button onClick={loadPreview} disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50">
        <Send className="h-3.5 w-3.5" /> {busy && !open ? 'Building preview…' : 'Send intro email'}
      </button>

      {open && preview && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <h4 className="font-semibold text-gray-900 text-sm">Intro email · {prospect.pha_name}</h4>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${preview.guardrail.live ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                {preview.guardrail.live ? 'LIVE · real recipients' : 'DEV · allowlisted inboxes only'}
              </span>
            </div>

            <div className="px-5 py-3 space-y-2 text-xs overflow-y-auto">
              {!preview.eligibility.ok && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded px-3 py-2">
                  Not eligible: {preview.eligibility.reasons.join('; ')}
                </div>
              )}
              {preview.eligibility.ok && !preview.guardrail.allowed && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">
                  Guardrail: {preview.guardrail.reason}
                </div>
              )}
              {!preview.guardrail.configured && (
                <div className="bg-gray-100 text-gray-700 rounded px-3 py-2">The mailer is not configured on this server.</div>
              )}
              {preview.eligibility.warnings.length > 0 && (
                <div className="text-amber-700">Heads up: {preview.eligibility.warnings.join('; ')}</div>
              )}
              <div className="grid grid-cols-[4rem_1fr] gap-y-1 text-gray-700">
                <span className="text-gray-400">To</span><span className="break-all">{preview.email.to.join(', ')}</span>
                <span className="text-gray-400">CC</span><span className="break-all">{preview.email.cc.join(', ') || '—'}</span>
                <span className="text-gray-400">Subject</span><span>{preview.email.subject}</span>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                {preview.email.text}
              </pre>
              {result && (
                <div className={`rounded px-3 py-2 ${result.ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  {result.text}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900">
                {result?.ok ? 'Close' : 'Cancel'}
              </button>
              {!result?.ok && (
                <button onClick={send} disabled={!canSend || busy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-40">
                  <Send className="h-3.5 w-3.5" /> {busy ? 'Sending…' : 'Confirm and send'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Expanded prospect row
// ═══════════════════════════════════════════════════════════
const ProspectDetail = ({ prospect, actor, onChanged }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState(prospect.notes || '');
  const [owner, setOwner] = useState(prospect.owner || '');
  const [followUp, setFollowUp] = useState(prospect.next_follow_up_on?.slice(0, 10) || '');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await outreachFetch(`/admin/prospects/${prospect.id}`);
      setEvents(data.events || []);
    } catch (err) {
      console.error('❌ Load prospect detail error:', err);
    } finally {
      setLoading(false);
    }
  }, [prospect.id]);

  useEffect(() => { load(); }, [load]);

  const logEvent = async (eventType) => {
    setSaving(true);
    try {
      await outreachFetch(`/admin/prospects/${prospect.id}/events`, {}, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventType, channel: eventType === 'call' ? 'phone' : 'email', actor }),
      });
      await load();
      onChanged();
    } catch (err) {
      console.error('❌ Log event error:', err);
      alert('Could not log that. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveFields = async () => {
    setSaving(true);
    try {
      await outreachFetch(`/admin/prospects/${prospect.id}/status`, {}, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes, owner, nextFollowUpOn: followUp, actor }),
      });
      onChanged();
    } catch (err) {
      console.error('❌ Save prospect error:', err);
      alert('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 px-5 py-4 bg-blue-50/30">
      {/* Facts */}
      <div className="space-y-2 text-xs text-gray-700">
        <div className="font-semibold text-gray-900 text-sm mb-1">{prospect.pha_name}</div>
        <div><span className="text-gray-500">PHA code</span> · {prospect.pha_code}</div>
        <div><span className="text-gray-500">Portfolio</span> · {fmt(prospect.portfolio_units)} units
          ({fmt(prospect.public_housing_units)} PH + {fmt(prospect.section8_units)} HCV)</div>
        <div><span className="text-gray-500">Annual expenses</span> · {money(prospect.annual_expenses)}</div>
        <div><span className="text-gray-500">PHAS</span> · {prospect.phas_designation || '—'}</div>
        {prospect.why_now && (
          <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
            <span className="text-gray-500 block mb-0.5">Why now</span>{prospect.why_now}
          </div>
        )}
        {(prospect.s8_email || prospect.s8_name) && (
          <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
            <span className="text-gray-500 block mb-0.5">Runs Section 8 / HCV</span>
            <div className="text-gray-900">
              {prospect.s8_name || 'name not confirmed'}
              {prospect.s8_title ? <span className="text-gray-500"> · {prospect.s8_title}</span> : null}
            </div>
            {prospect.s8_email && (
              <a href={`mailto:${prospect.s8_email}`} className="text-blue-600 hover:text-blue-800 break-all">
                {prospect.s8_email}
              </a>
            )}
            {prospect.s8_phone && (
              <div className="text-gray-700 flex items-center gap-1"><Phone className="h-3 w-3" /> {prospect.s8_phone}</div>
            )}
            {prospect.s8_grade && (
              <div className="text-gray-400 mt-0.5">
                {{
                  personal: 'email seen on the site next to the name',
                  inferred: 'name and title from the staff page; email follows the agency pattern',
                  'inferred-weak': 'name from the staff page; email is a best-guess pattern — may bounce',
                  'name-only': 'name from the staff page; no email pattern known — call',
                  adjacent: 'email next to a Section 8 title; name unconfirmed',
                  role: 'program inbox, no person',
                }[prospect.s8_grade] || prospect.s8_grade}
              </div>
            )}
          </div>
        )}
        {prospect.other_leaders && (
          <div><span className="text-gray-500">Also on the leadership page</span> · {prospect.other_leaders}</div>
        )}
        <div><span className="text-gray-500">Name source</span> · {prospect.name_source || 'not crawled'}</div>
        {prospect.website && (
          <a href={prospect.website} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800">
            <Globe className="h-3.5 w-3.5" /> {prospect.website.replace(/^https?:\/\//, '')}
          </a>
        )}
      </div>

      {/* Work it */}
      <div className="space-y-3">
        <SendIntroEmail prospect={prospect} actor={actor} onSent={() => { load(); onChanged(); }} />
        <div>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Log a touch</span>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {['email_sent', 'call', 'meeting', 'reply_received', 'note', 'unsubscribe'].map((type) => {
              const meta = EVENT_META[type];
              const Icon = meta.icon;
              return (
                <button key={type} onClick={() => logEvent(type)} disabled={saving}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-gray-200 bg-white text-xs text-gray-700 hover:border-blue-400 hover:text-blue-700 disabled:opacity-50">
                  <Icon className={`h-3.5 w-3.5 ${meta.tone}`} /> {meta.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs text-gray-500">Owner</span>
            <input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="who owns this"
              className="mt-0.5 w-full px-2 py-1 text-sm border border-gray-200 rounded" />
          </label>
          <label className="block">
            <span className="text-xs text-gray-500">Follow up on</span>
            <input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)}
              className="mt-0.5 w-full px-2 py-1 text-sm border border-gray-200 rounded" />
          </label>
        </div>

        <label className="block">
          <span className="text-xs text-gray-500">Notes</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            className="mt-0.5 w-full px-2 py-1 text-sm border border-gray-200 rounded" />
        </label>

        <button onClick={saveFields} disabled={saving}
          className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* History */}
      <div>
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">History</span>
        {loading ? (
          <p className="text-xs text-gray-400 mt-2">Loading…</p>
        ) : events.length === 0 ? (
          <p className="text-xs text-gray-400 mt-2">No activity yet.</p>
        ) : (
          <div className="mt-2 space-y-2 max-h-56 overflow-y-auto pr-1">
            {events.map((e) => {
              const meta = EVENT_META[e.event_type] || EVENT_META.note;
              const Icon = meta.icon;
              return (
                <div key={e.id} className="flex items-start gap-2 text-xs">
                  <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${meta.tone}`} />
                  <div className="min-w-0">
                    <div className="text-gray-900">
                      {meta.label}
                      {e.event_type === 'status_change' && e.to_status
                        ? ` · ${STATUS_META[e.from_status]?.label || e.from_status} → ${STATUS_META[e.to_status]?.label || e.to_status}`
                        : ''}
                      {e.subject && (e.event_type === 'email_sent' || e.event_type === 'reply_received') ? ` · ${e.subject}` : ''}
                    </div>
                    <div className="text-gray-400">{fmtDate(e.occurred_at)}{e.actor ? ` · ${e.actor}` : ''}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Main tab
// ═══════════════════════════════════════════════════════════
const OutreachTab = ({ currentUser }) => {
  const [stats, setStats] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    tier: 'all', state: 'all', status: 'all', named: 'all',
    needsReview: 'all', hasS8: 'all', sort: 'units', q: '',
  });
  const LIMIT = 50;

  const loadStats = useCallback(async () => {
    try {
      setStats(await outreachFetch('/admin/stats'));
    } catch (err) {
      console.error('❌ Load outreach stats error:', err);
      setError('Failed to load outreach statistics.');
    }
  }, []);

  const loadProspects = useCallback(async (pageNum, f) => {
    setListLoading(true);
    try {
      const data = await outreachFetch('/admin/prospects', { ...f, page: pageNum, limit: LIMIT });
      setProspects(data.prospects || []);
      setTotal(data.pagination?.total || 0);
      setPage(pageNum);
    } catch (err) {
      console.error('❌ Load prospects error:', err);
      setError('Failed to load prospects.');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadStats(), loadProspects(1, filters)]);
      setLoading(false);
    })();
    // Initial load only; filter changes go through applyFilters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (next) => {
    const merged = { ...filters, ...next };
    setFilters(merged);
    setExpanded(null);
    loadProspects(1, merged);
  };

  const changeStatus = async (id, status) => {
    setSavingId(id);
    try {
      await outreachFetch(`/admin/prospects/${id}/status`, {}, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, actor: currentUser }),
      });
      setProspects((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
      loadStats();
    } catch (err) {
      console.error('❌ Status change error:', err);
      alert('Could not change the stage. Please try again.');
    } finally {
      setSavingId(null);
    }
  };

  const refreshAll = () => {
    loadStats();
    loadProspects(page, filters);
  };

  const states = useMemo(
    () => (stats?.byState || []).map((r) => r.state),
    [stats]
  );

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
        <p className="text-red-700">{error}</p>
        <button onClick={refreshAll} className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-sm">
          <RefreshCw className="h-4 w-4" /> Retry
        </button>
      </div>
    );
  }

  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="space-y-6">
      {stats?.mailer && (
        <div className={`rounded-lg px-4 py-2 text-xs flex items-center gap-2 border ${
          stats.mailer.live ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
          <Mail className="h-3.5 w-3.5 shrink-0" />
          {stats.mailer.configured
            ? <>Outreach mailbox <span className="font-mono">{stats.mailer.mailbox}</span> · {stats.mailer.live
                ? <strong>LIVE: emails go to real prospects</strong>
                : 'DEV: only allowlisted test inboxes can be addressed'}
                {stats.mailer.excludedStates?.length ? ` · excluded states: ${stats.mailer.excludedStates.join(', ')}` : ''}</>
            : 'Outreach mailer is not configured on this server.'}
        </div>
      )}
      {stats && <OutreachStats stats={stats} />}
      {stats?.mailer?.configured && (
        <SchedulerPanel actor={currentUser} onChanged={() => { loadStats(); loadProspects(page, filters); }} />
      )}
      {stats?.mailer?.configured && (
        <InboxPanel actor={currentUser} onChanged={() => { loadStats(); loadProspects(page, filters); }} />
      )}

      {/* Prospect list */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters({ q: search })}
              placeholder="Agency, director, email, PHA code…"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg"
            />
          </div>

          <select value={filters.tier} onChange={(e) => applyFilters({ tier: e.target.value })}
            className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg">
            <option value="all">All tiers</option>
            {['A', 'B', 'C', 'D'].map((t) => (
              <option key={t} value={t}>Tier {t} · {TIER_META[t].hint}</option>
            ))}
          </select>

          <select value={filters.status} onChange={(e) => applyFilters({ status: e.target.value })}
            className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg">
            <option value="all">All stages</option>
            {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>

          <select value={filters.state} onChange={(e) => applyFilters({ state: e.target.value })}
            className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg">
            <option value="all">All states</option>
            {states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>

          <select value={filters.named} onChange={(e) => applyFilters({ named: e.target.value })}
            className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg">
            <option value="all">Named or not</option>
            <option value="yes">Has a name</option>
            <option value="no">No name yet</option>
          </select>

          <select value={filters.sort} onChange={(e) => applyFilters({ sort: e.target.value })}
            className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg">
            <option value="units">Biggest first</option>
            <option value="name">A–Z</option>
            <option value="state">By state</option>
            <option value="tier">By tier</option>
            <option value="recent">Recently touched</option>
            <option value="due">Follow-up due</option>
          </select>

          <button onClick={() => applyFilters({ hasS8: filters.hasS8 === 'yes' ? 'all' : 'yes' })}
            className={`px-2.5 py-1.5 text-sm rounded-lg border ${filters.hasS8 === 'yes'
              ? 'bg-blue-100 border-blue-300 text-blue-800'
              : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            Has S8 contact
          </button>

          <button onClick={() => applyFilters({ needsReview: filters.needsReview === 'yes' ? 'all' : 'yes' })}
            className={`px-2.5 py-1.5 text-sm rounded-lg border ${filters.needsReview === 'yes'
              ? 'bg-amber-100 border-amber-300 text-amber-800'
              : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            Needs review
          </button>

          <a href={outreachUrl('/admin/export.csv', filters)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">
            <Download className="h-4 w-4" /> CSV
          </a>

          <button onClick={refreshAll}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-600 hover:text-gray-900">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>

        {listLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : prospects.length === 0 ? (
          <div className="p-10 text-center text-gray-500">No prospects match those filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-gray-600">
                  <th className="px-3 py-2.5 font-semibold">Housing authority</th>
                  <th className="px-3 py-2.5 font-semibold">Executive director</th>
                  <th className="px-3 py-2.5 font-semibold">Contact</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Units</th>
                  <th className="px-3 py-2.5 font-semibold">Tier</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Touches</th>
                  <th className="px-3 py-2.5 font-semibold">Last / due</th>
                  <th className="px-3 py-2.5 font-semibold">Stage</th>
                </tr>
              </thead>
              <tbody>
                {prospects.map((p) => (
                  <React.Fragment key={p.id}>
                    <tr className="border-b border-gray-100 hover:bg-blue-50/40 cursor-pointer"
                      onClick={() => setExpanded(expanded === p.id ? null : p.id)}>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-gray-900 flex items-center gap-1.5">
                          {p.pha_name}
                          {p.is_test && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">TEST</span>}
                        </div>
                        <div className="text-xs text-gray-500">{p.city}, {p.state} · {p.pha_code}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        {p.exec_name ? (
                          <>
                            <div className="text-gray-900">{p.exec_name}</div>
                            <div className="text-xs text-gray-500 flex items-center gap-1.5">
                              {p.exec_title || '—'}
                              {p.name_confidence && (
                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${CONFIDENCE_META[p.name_confidence]?.badge || ''}`}>
                                  {CONFIDENCE_META[p.name_confidence]?.label}
                                </span>
                              )}
                            </div>
                          </>
                        ) : (
                          <span className="text-gray-400 text-xs">no name · {p.name_source || 'not crawled'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs">
                        {p.exec_dir_email ? (
                          <a href={`mailto:${p.exec_dir_email}`} onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 hover:text-blue-800 break-all">{p.exec_dir_email}</a>
                        ) : <span className="text-gray-400">—</span>}
                        <div className="text-gray-500">{p.exec_dir_phone || p.agency_phone || ''}</div>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmt(p.portfolio_units)}</td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${(TIER_META[p.icp_tier] || TIER_META.D).badge}`}>
                          {p.icp_tier}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{p.touches}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
                        <div>{p.last_contacted_at ? fmtDate(p.last_contacted_at) : '—'}</div>
                        {p.next_follow_up_on && (
                          <div className={new Date(p.next_follow_up_on) <= new Date() ? 'text-red-600' : 'text-gray-400'}>
                            due {fmtDate(p.next_follow_up_on)}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <select value={p.status} disabled={savingId === p.id}
                          onChange={(e) => changeStatus(p.id, e.target.value)}
                          className={`text-xs font-medium rounded-full px-2.5 py-1 border-0 cursor-pointer ${STATUS_META[p.status]?.badge || 'bg-gray-100 text-gray-700'}`}>
                          {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                        </select>
                      </td>
                    </tr>
                    {expanded === p.id && (
                      <tr className="border-b border-blue-100">
                        <td colSpan={8} className="p-0">
                          <ProspectDetail prospect={p} actor={currentUser} onChanged={refreshAll} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between p-3 border-t border-gray-100 text-sm text-gray-600">
          <span>{fmt(total)} prospects · page {page} of {fmt(pages)}</span>
          <div className="flex gap-1">
            <button disabled={page <= 1} onClick={() => loadProspects(page - 1, filters)}
              className="p-1.5 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button disabled={page >= pages} onClick={() => loadProspects(page + 1, filters)}
              className="p-1.5 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-400">
        Roster from HUD's PUBLIC_HOUSING_AUTHORITY layer, director names recovered by the outreach crawler.
        Contact data is public record — CAN-SPAM still applies, and many PHAs bar unsolicited vendor pitches
        outside a posted RFP.
      </p>
    </div>
  );
};

export default OutreachTab;
